import React from 'react';
import { useFormContext } from 'react-hook-form';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CitySearchSelect } from '@/components/CitySearchSelect';

import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';

import type { FormField as FieldDefinition } from '../types';

interface DynamicFieldProps {
    field: FieldDefinition;
}

export function DynamicField({ field }: DynamicFieldProps) {
    const { control, watch, formState } = useFormContext();

    // Lógica para campos condicionales
    if (field.conditional) {
        const dependentValue = watch(field.conditional.field);
        const { operator, value } = field.conditional;

        let isVisible = false;
        switch (operator) {
            case '==': isVisible = dependentValue === value; break;
            case '!=': isVisible = dependentValue !== value; break;
            case '>': isVisible = dependentValue > value; break;
            case '<': isVisible = dependentValue < value; break;
            case 'in': isVisible = Array.isArray(value) && value.includes(dependentValue); break;
            case 'contains': isVisible = Array.isArray(dependentValue) && dependentValue.includes(value); break;
        }

        if (!isVisible) return null;
    }

    return (
        <FormField
            control={control}
            name={field.id}
            render={({ field: formField, fieldState }) => {
                // Only show error if form has been submitted
                const shouldShowError = formState.isSubmitted;


                return (
                    <FormItem className={cn("w-full", field.type === 'checkbox' && "flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4")}>
                        {field.type !== 'checkbox' && (
                            <FormLabel>
                                {field.label}
                                {field.required && <span className="text-destructive ml-1">*</span>}
                            </FormLabel>
                        )}

                        <FormControl>
                            {renderInput(field, formField)}
                        </FormControl>

                        {field.type === 'checkbox' && (
                            <div className="space-y-1 leading-none">
                                <FormLabel>
                                    {field.label}
                                    {field.required && <span className="text-destructive ml-1">*</span>}
                                </FormLabel>
                            </div>
                        )}

                        {field.placeholder && field.type !== 'select' && field.type !== 'date' && (
                            <FormDescription>{field.placeholder}</FormDescription>
                        )}

                        {shouldShowError && <FormMessage />}
                    </FormItem>
                );
            }}
        />
    );
}

function renderInput(definition: FieldDefinition, formField: any) {
    switch (definition.type) {
        case 'text':
        case 'email':
        case 'tel':
        case 'number':
            return (
                <Input
                    type={definition.type}
                    placeholder={definition.placeholder}
                    {...formField}
                    onChange={(e) => {
                        const val = definition.type === 'number' ?
                            (e.target.value === '' ? '' : Number(e.target.value)) :
                            e.target.value;
                        formField.onChange(val);
                    }}
                />
            );

        case 'textarea':
            return (
                <Textarea
                    placeholder={definition.placeholder}
                    className="resize-none"
                    {...formField}
                />
            );

        case 'select':
            return (
                <Select onValueChange={formField.onChange} defaultValue={formField.value}>
                    <SelectTrigger type="button">
                        <SelectValue placeholder={definition.placeholder || "Seleccionar..."} />
                    </SelectTrigger>
                    <SelectContent>
                        {definition.options?.map((option) => (
                            <SelectItem key={option.value} value={String(option.value)}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );

        case 'radio':
            return (
                <RadioGroup
                    onValueChange={formField.onChange}
                    defaultValue={formField.value}
                    className="flex flex-col space-y-1"
                >
                    {definition.options?.map((option) => (
                        <FormItem key={option.value} className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                                <RadioGroupItem value={String(option.value)} type="button" />
                            </FormControl>
                            <FormLabel className="font-normal">
                                {option.label}
                            </FormLabel>
                        </FormItem>
                    ))}
                </RadioGroup>
            );

        case 'checkbox':
            return (
                <Checkbox
                    checked={formField.value}
                    onCheckedChange={formField.onChange}
                    type="button"
                />
            );

        case 'date':
            return (
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            variant={"outline"}
                            type="button"
                            className={cn(
                                "w-full pl-3 text-left font-normal",
                                !formField.value && "text-muted-foreground"
                            )}
                        >
                            {formField.value ? (
                                format(new Date(formField.value), "PPP", { locale: es })
                            ) : (
                                <span>{definition.placeholder || "Seleccionar fecha"}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={formField.value ? new Date(formField.value) : undefined}
                            onSelect={(date) => formField.onChange(date?.toISOString())}
                            disabled={(date) =>
                                date > new Date() || date < new Date("1900-01-01")
                            }
                            initialFocus
                        />
                    </PopoverContent>
                </Popover>
            );

        case 'range':
            // Para range asumimos que el valor es un objeto { min, max } o un array [min, max]
            // Si es un solo valor, usamos Slider simple
            const val = Array.isArray(formField.value) ? formField.value : [0];
            const max = definition.validation?.max || 100;
            const min = definition.validation?.min || 0;

            return (
                <div className="pt-2">
                    <Slider
                        defaultValue={val}
                        max={max}
                        min={min}
                        step={1}
                        onValueChange={formField.onChange}
                    />
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                        <span>{min}</span>
                        <span>{formField.value}</span>
                        <span>{max}</span>
                    </div>
                </div>
            );

        case 'city':
            return <CitySelector field={definition} formField={formField} />;

        case 'lookup':
            return <LookupSelector field={definition} formField={formField} />;

        default:
            return <Input {...formField} />;
    }
}

function CitySelector({ field, formField }: { field: FieldDefinition, formField: any }) {
    return (
        <CitySearchSelect
            value={formField.value}
            onChange={formField.onChange}
            placeholder={field.placeholder || "Seleccionar ciudad..."}
        />
    );
}

function LookupSelector({ field, formField }: { field: FieldDefinition, formField: any }) {
    const { data: snapshot } = useLookupsSnapshot();

    const options = React.useMemo(() => {
        if (!snapshot || !field.lookupType) return [];

        let rawItems: any[] = [];
        switch (field.lookupType) {
            case 'species': rawItems = snapshot.species || []; break;
            case 'sex': rawItems = snapshot.sexes || []; break;
            case 'size': rawItems = snapshot.sizes || []; break;
            case 'breed': rawItems = snapshot.breeds || []; break;
            case 'color': rawItems = snapshot.colors || []; break;
            case 'status': rawItems = snapshot.statuses || []; break;
        }

        let items = rawItems.map((i: any) => ({
            value: i.id,
            label: toOptionLabel(i),
            raw: i
        }));

        // Custom sorting
        if (field.lookupType === 'species') {
            items.sort((a: any, b: any) => {
                const priority = ['perro', 'gato'];
                const aIdx = priority.indexOf(a.label.toLowerCase());
                const bIdx = priority.indexOf(b.label.toLowerCase());

                if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                if (aIdx !== -1) return -1;
                if (bIdx !== -1) return 1;
                return a.label.localeCompare(b.label);
            });
        } else if (field.lookupType === 'size') {
            const sizeOrder = ['miniatura', 'toy', 'pequeño', 'mediano', 'grande', 'gigante'];
            items.sort((a: any, b: any) => {
                const getOrder = (label: string) => {
                    const lower = label.toLowerCase();
                    const idx = sizeOrder.findIndex(s => lower.includes(s));
                    return idx === -1 ? 999 : idx;
                };
                return getOrder(a.label) - getOrder(b.label);
            });
        }

        return items;
    }, [snapshot, field.lookupType]);

    return (
        <Select onValueChange={(val) => formField.onChange(Number(val))} defaultValue={formField.value ? String(formField.value) : undefined}>
            <SelectTrigger type="button">
                <SelectValue placeholder={field.placeholder || "Seleccionar..."} />
            </SelectTrigger>
            <SelectContent>
                {options.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
